import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const PayoutsComponent = ({ user }) => {
  const [tab, setTab] = useState('account');
  const [account, setAccount] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, paid: 0 });
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [onboardForm, setOnboardForm] = useState({
    business_name: '', country: 'VE', payout_method: 'bank_transfer',
    bank_name: '', account_last4: '', account_holder: ''
  });
  const [adminData, setAdminData] = useState(null);

  const isAdmin = user?.email === 'admin@cualapp.com';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [accRes, earnRes, payRes] = await Promise.all([
        axios.get(`${API}/payouts/seller/account`),
        axios.get(`${API}/payouts/seller/earnings`),
        axios.get(`${API}/payouts/history`)
      ]);
      setAccount(accRes.data.account);
      setEarnings(earnRes.data.earnings || []);
      setSummary(earnRes.data.summary || { total: 0, pending: 0, paid: 0 });
      setPayouts(payRes.data.payouts || []);

      if (isAdmin) {
        const adminRes = await axios.get(`${API}/payouts/admin/all-payouts`);
        setAdminData(adminRes.data);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const onboard = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await axios.post(`${API}/payouts/seller/onboard`, onboardForm);
      alert('Cuenta de vendedor configurada');
      setShowOnboard(false);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const requestPayout = async () => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt < 5) { alert('Monto minimo: $5 USD'); return; }
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/payouts/request-payout`, { amount: amt });
      alert(res.data.message);
      setPayoutAmount('');
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="payouts-module">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payouts</h2>
          <p className="text-sm text-gray-500">Sistema de pagos para vendedores via Stripe Connect</p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200" data-testid="test-mode-badge">TEST MODE</span>
      </div>

      {/* Account status card */}
      {account ? (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <i className="fa fa-check text-green-600"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">{account.business_name}</p>
                <p className="text-xs text-gray-400">ID: {account.stripe_connect_id}</p>
              </div>
            </div>
            <span className="px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700">Activa</span>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-100">
              <p className="text-2xl font-bold text-green-700">${summary.total.toFixed(2)}</p>
              <p className="text-xs text-green-600">Total Ganado</p>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 text-center border border-amber-100">
              <p className="text-2xl font-bold text-amber-700">${summary.pending.toFixed(2)}</p>
              <p className="text-xs text-amber-600">Pendiente</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 text-center border border-blue-100">
              <p className="text-2xl font-bold text-blue-700">${summary.paid.toFixed(2)}</p>
              <p className="text-xs text-blue-600">Pagado</p>
            </div>
          </div>

          {/* Payout request */}
          {summary.pending > 0 && (
            <div className="mt-4 flex items-center gap-3 bg-gray-50 rounded-xl p-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Solicitar Payout (min $5 USD)</label>
                <input type="number" step="0.01" value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)}
                  placeholder={`Max: $${summary.pending.toFixed(2)}`} data-testid="payout-amount-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <button onClick={requestPayout} disabled={processing} data-testid="request-payout-btn"
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 mt-5">
                {processing ? 'Procesando...' : 'Solicitar'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
          <i className="fa fa-university text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-600 font-medium">No tienes cuenta de vendedor</p>
          <p className="text-sm text-gray-400 mb-4">Registrate para recibir pagos por tus ventas en el marketplace</p>
          <button onClick={() => setShowOnboard(true)} data-testid="onboard-btn"
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700">
            Configurar Cuenta de Vendedor
          </button>
        </div>
      )}

      {/* Onboard form */}
      {showOnboard && (
        <div className="bg-white rounded-xl border border-green-200 p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurar Cuenta de Pagos</h3>
          <form onSubmit={onboard} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Negocio</label>
                <input type="text" required value={onboardForm.business_name} onChange={e => setOnboardForm({...onboardForm, business_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Mi Tienda" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pais</label>
                <select value={onboardForm.country} onChange={e => setOnboardForm({...onboardForm, country: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="VE">Venezuela</option><option value="PA">Panama</option><option value="CO">Colombia</option>
                  <option value="BR">Brasil</option><option value="MX">Mexico</option><option value="CL">Chile</option><option value="AR">Argentina</option><option value="PE">Peru</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                <input type="text" value={onboardForm.bank_name} onChange={e => setOnboardForm({...onboardForm, bank_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Banesco, Mercantil..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ultimos 4 digitos de cuenta</label>
                <input type="text" maxLength="4" value={onboardForm.account_last4} onChange={e => setOnboardForm({...onboardForm, account_last4: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="1234" />
              </div>
            </div>
            <button type="submit" disabled={processing} data-testid="submit-onboard-btn"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-2.5 rounded-lg font-medium disabled:opacity-50">
              {processing ? 'Registrando...' : 'Activar Cuenta de Vendedor'}
            </button>
          </form>
        </div>
      )}

      {/* Tabs */}
      {account && (
        <>
          <div className="flex gap-2">
            {['account', 'earnings', 'payouts', ...(isAdmin ? ['admin'] : [])].map(t => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {t === 'account' && 'Cuenta'}
                {t === 'earnings' && `Ganancias (${earnings.length})`}
                {t === 'payouts' && `Payouts (${payouts.length})`}
                {t === 'admin' && 'Admin'}
              </button>
            ))}
          </div>

          {/* Earnings tab */}
          {tab === 'earnings' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Historial de Ganancias</h3></div>
              {earnings.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Sin ganancias registradas</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {earnings.map(e => (
                    <div key={e.id} className="px-6 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{e.description || e.source}</p>
                        <p className="text-xs text-gray-400">{new Date(e.created_at).toLocaleString('es-ES')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-700">+{e.amount} {e.currency}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${e.payout_status === 'paid' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                          {e.payout_status === 'paid' ? 'Pagado' : 'Pendiente'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payouts tab */}
          {tab === 'payouts' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Historial de Payouts</h3></div>
              {payouts.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Sin payouts realizados</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {payouts.map(p => (
                    <div key={p.id} className="px-6 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Payout #{p.id.slice(0, 8)}</p>
                        <p className="text-xs text-gray-400">{new Date(p.created_at).toLocaleString('es-ES')}</p>
                        <p className="text-xs text-gray-400">Fee: ${p.platform_fee} ({p.platform_fee_percent}%)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-blue-700">${p.net_amount} USD</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${p.status === 'processing' ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                          {p.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Account details tab */}
          {tab === 'account' && account && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Detalles de Cuenta</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Negocio</p>
                  <p className="font-medium text-gray-900">{account.business_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Pais</p>
                  <p className="font-medium text-gray-900">{account.country}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Banco</p>
                  <p className="font-medium text-gray-900">{account.bank_info?.bank_name || 'No configurado'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Cuenta (ultimos 4)</p>
                  <p className="font-medium text-gray-900">****{account.bank_info?.account_number_last4 || '----'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Stripe Connect ID</p>
                  <p className="font-mono text-xs text-gray-600">{account.stripe_connect_id}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">Total Viajes/Ventas</p>
                  <p className="font-medium text-gray-900">{account.payout_count} payouts</p>
                </div>
              </div>
            </div>
          )}

          {/* Admin tab */}
          {tab === 'admin' && isAdmin && adminData && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Admin - Resumen de Payouts</h3>
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-indigo-50 rounded-xl p-3 text-center border border-indigo-100">
                  <p className="text-xl font-bold text-indigo-700">{adminData.stats.total_sellers}</p>
                  <p className="text-xs text-indigo-600">Vendedores</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                  <p className="text-xl font-bold text-green-700">{adminData.stats.total_payouts}</p>
                  <p className="text-xs text-green-600">Payouts</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
                  <p className="text-xl font-bold text-blue-700">${adminData.stats.total_paid_usd}</p>
                  <p className="text-xs text-blue-600">Total Pagado</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
                  <p className="text-xl font-bold text-amber-700">${adminData.stats.total_platform_fees}</p>
                  <p className="text-xs text-amber-600">Fees Plataforma</p>
                </div>
              </div>

              {adminData.seller_accounts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Cuentas de Vendedor</h4>
                  <div className="space-y-2">
                    {adminData.seller_accounts.map(a => (
                      <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{a.business_name} ({a.user_email})</p>
                          <p className="text-xs text-gray-400">{a.country} | {a.stripe_connect_id}</p>
                        </div>
                        <div className="text-right text-xs">
                          <p className="text-green-600 font-medium">Ganado: ${a.total_earned}</p>
                          <p className="text-blue-600">Pagado: ${a.total_paid_out}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayoutsComponent;
